/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ApolloError } from "@apollo/client";
import { ColumnDef } from "@tanstack/react-table";
import { When } from "react-if";
import { Spacer, theme } from "@nordcloud/gnui";
import {
  ChartType,
  LinkParameters,
  Period,
  TableData,
} from "~/components/Charts/CostAnalysis/types";
import { ErrorWrap, LoaderWrap } from "~/components/Utils";
import { CostAnalysisCostTable } from "../CostAnalysisCostTable";
import { ChartPeriodData } from "./ChartPeriodData";
import { TotalCosts } from "./TotalCosts";

type Props = {
  period: Period;
  startDate: string;
  endDate: string;
  tableData: TableData[];
  chartType: ChartType;
  total: number;
  columns: ColumnDef<TableData>[];
  isLoading: boolean;
  showCostTable: boolean;
  hasFooter: boolean;
  linkParameters?: LinkParameters;
  tableError?: ApolloError;
  hideHeader?: boolean;
  getSubRows?: (
    originalRow: TableData,
    index: number
  ) => TableData[] | undefined;
};

export function CostTableData({
  period,
  startDate,
  endDate,
  tableData,
  chartType,
  total,
  columns,
  isLoading,
  showCostTable,
  hasFooter,
  tableError,
  linkParameters,
  hideHeader,
  getSubRows,
}: Props) {
  return (
    <>
      <When condition={showCostTable || shouldShowPeriod(chartType, period)}>
        <ChartPeriodData
          period={period}
          startDate={startDate}
          endDate={endDate}
          chartType={chartType}
        />
      </When>
      <Spacer height={theme.spacing.spacing04} />
      <LoaderWrap inContent loading={isLoading}>
        <When condition={shouldShowTotalCost(chartType, period) && !hideHeader}>
          <TotalCosts
            tableData={tableData}
            chartTypeValue={chartType}
            total={total}
            linkParameters={linkParameters}
          />
        </When>
        <When condition={showCostTable}>
          <ErrorWrap error={tableError}>
            <div css={{ width: "100%", overflow: "scroll" }}>
              <CostAnalysisCostTable
                columns={columns}
                data={tableData}
                hasFooter={hasFooter}
                hideHeader={hideHeader}
                getSubRows={getSubRows}
                period={period}
              />
            </div>
          </ErrorWrap>
        </When>
      </LoaderWrap>
    </>
  );
}

function shouldShowTotalCost(chartType: ChartType, period: Period) {
  const showTotalForMonth = [
    ChartType.COST_PER_ENVIRONMENT,
    ChartType.COST_PER_CATEGORY,
    ChartType.COST_PER_APPLICATION,
  ].includes(chartType);

  return period === Period.MONTH && showTotalForMonth;
}

function shouldShowPeriod(chartType: ChartType, period: Period) {
  if (period !== Period.MONTH) {
    return true;
  }

  return [
    ChartType.COST_PER_ENVIRONMENT,
    ChartType.COST_PER_CATEGORY,
    ChartType.COST_PER_APPLICATION,
    ChartType.ANOMALY_COST,
  ].includes(chartType);
}
